import { observer } from "mobx-react-lite";
import { Flex } from "@chakra-ui/react";

import { Lang } from "@noli/core";
import { apiSchema } from "@noli/svg";
import {
  langStore,
  selectingNodesStore,
  SelectingTextParams,
  translate,
  useEditText,
  useSelecting,
} from "@noli/business";

import type { ISubPanel } from "@/components/SubPanel";
import { Editor_Words } from "@/utils/lang/editor";
import SubPanelWithScrollBar from "@/components/SubPanel/WithScrollBar";
import TextArea from "@/components/TextArea";
import SchemaProperty from "@/components/Property/SchemaProperty";
import SubEditorPanel from "@/components/SubPanel/SubEditor";

type TextEditorProps = ISubPanel & {
  alias: string;
  lang: Lang;
  textParams: SelectingTextParams;
  res: ReturnType<typeof useEditText>;
};

function TextContentEditor({ lang, textParams, res: { editContent }, ...others }: TextEditorProps) {
  const content = textParams.content;
  return (
    <SubEditorPanel panelTitle={translate("text-content-title", lang)} {...others}>
      <TextArea
        w="85%"
        lang={lang}
        textAreaH={160}
        placeholderWord={Editor_Words["text-placeholder"]}
        onBlur={() => editContent({ trace: true })(content)}
        text={content}
        setText={editContent({ trace: false })}
      />
    </SubEditorPanel>
  );
}

function TextBasicEditor({
  alias,
  lang,
  textParams,
  res: { editFontSize },
  ...others
}: TextEditorProps) {
  const { fontSize: fontSizeSchema } = apiSchema.noliTextFrame.options;

  const inputProps = { noSlider: true, w: "40%", ml: "16px", captionW: "48px" };

  return (
    <SubEditorPanel panelTitle={translate("text-settings-title", lang)} pb="16px" {...others}>
      <Flex w="100%" wrap="wrap">
        <SchemaProperty
          mt="4px"
          identifier={`${alias}-fontSize`}
          {...inputProps}
          apiKey="fontSize"
          apiOption={fontSizeSchema}
          value={textParams.fontSize}
          setValue={editFontSize}
          lang={lang}
        />
      </Flex>
    </SubEditorPanel>
  );
}

function TextEditor() {
  const res = useEditText();
  const lang = langStore.tgt;
  const { type, displayNode } = useSelecting("raw");
  const textParams = selectingNodesStore.info.textParams;
  if (type !== "text" || !textParams) return null;
  const alias = displayNode.alias;
  return (
    <SubPanelWithScrollBar w="100%" h="100%">
      <TextContentEditor alias={alias} lang={lang} textParams={textParams} res={res} h="250px" />
      <TextBasicEditor alias={alias} lang={lang} textParams={textParams} res={res} />
    </SubPanelWithScrollBar>
  );
}

export default observer(TextEditor);
